import { FC } from 'react';
import {
  Container,
  Link,
  List,
  ListItem,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Theme,
  Typography,
} from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import { Link as RouterLink } from 'react-router-dom';

import Meta from '@bizapp-lp/pjb/organisms/Meta';
import { Terms } from '@bizapp-lp/pjb/templates/Terms';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    note: {
      margin: '48px 0 0 0 !important',
    },
    innerList: {
      '& > li': {
        listStyle: 'none',
        marginLeft: theme.spacing(-4),
      },
    },
    noteList: {
      margin: '8px 0 0 !important',
      padding: '0 !important',
      '& li': {
        position: 'static',
        listStyle: 'none',
        marginTop: '-13px !important',
        marginBottom: '0 !important',
        '&:before': {
          content: "'※'",
          position: 'relative',
          top: '21px',
          left: '-14px',
          fontSize: '14px',
          lineSpacing: '21px',
        },
      },
    },
    lineBreak: {
      '& span': {
        display: 'block',
      },
    },
  }),
);

const title =
  'Project Boardサービス仕様書 | 株式会社ワークスアプリケーションズ';
const description =
  '株式会社ワークスアプリケーションズがご提供する、Project Boardサービス仕様書です。';

const ServiceSpec: FC = () => {
  const classes = useStyles();

  const termsProps = {
    pageTitle: (
      <>
        <span style={{ display: 'block' }}>HUE Works Suite</span>
        <span style={{ display: 'block' }}>Project Boardサービス仕様書</span>
      </>
    ),
    headerContent: '',
  };
  return (
    <>
      <Meta>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
      </Meta>
      <Terms {...termsProps}>
        <Container disableGutters maxWidth={false}>
          <Typography>
            これは過去のHUE Works Suite Project
            Boardサービス仕様書です。最新版は
            <Link href="https://www.worksap.co.jp/services/spec/pjb-spec/">
              こちら
            </Link>
            です。
          </Typography>
          <Typography variant="h2">1. 本書の定義</Typography>
          <Typography variant="body1">
            本書は、お客様におけるHUE Works Suite Project Board（以下、 Project
            Board）利用にあたり弊社（株式会社ワークスアプリケーションズ・エンタープライズ又は株式会社ワークスアプリケーションズ・システムズのうち、お客様による利用申込に対して承諾した法人をいいます。）より提供可能なクラウドサービスの内容を記述したものです。Project
            Boardの利用に際しては、本内容および「HUE Works Suite / HUE Works
            Suite DX
            Solutions利用規約」（以下、利用規約）に同意していただく必要があります。
          </Typography>
          <Typography variant="body1">
            本書と利用規約の記載内容が異なる場合には利用規約の内容が優先されます。
          </Typography>
          <Typography variant="body1">
            過去に提示済みのサービス内容と内容が異なる場合には、常に最新のサービス内容が優先して適用されます。
          </Typography>
        </Container>

        <Container disableGutters maxWidth={false}>
          <Typography variant="h2">2. Project Boardサービス概要</Typography>
          <Typography variant="body1">
            Project
            Boardはオンラインシェア型プロジェクト進捗管理サービスです。プロジェクト管理でよく利用されるガントチャート機能やかんばん機能を搭載し、チームや組織、外部ユーザーとのコラボレーションによる業務の効率化をサポートします。
          </Typography>
          <Typography variant="body1">
            ※Project Boardは法人、団体又は個人のお客様に向けたサービスです。
          </Typography>
          <Typography variant="body1">
            ※Project
            Boardでは外部ユーザーをゲストユーザーとして招待し、共同で利用することが可能です。ただし、当該外部ユーザーによるProject
            Board利用に関連する一切の行為はお客様による行為とみなすこととし、当該外部ユーザーによる利用規約および本サービス仕様書の違反についてはお客様による違反としてお客様が弊社に対して一切の責任を負うものとします。
          </Typography>

          <Container disableGutters maxWidth={false}>
            <Typography variant="h3">2.1 標準サービス機能</Typography>
            <Typography variant="body1">
              Project
              Boardでは下記機能を提供いたします。あわせて、初期構築作業（利用環境構築作業および管理者アカウント作成作業）を提供いたします。
            </Typography>
            <Typography variant="body1">
              また、下記機能および初期構築作業の仕様、品質その他一切の内容に関する詳細は、弊社がその裁量によって決定するものとします。
            </Typography>
            <List component="ul">
              <ListItem>
                <ListItemText>管理機能</ListItemText>
                <List component="ul" className={classes.innerList}>
                  <ListItem>
                    <ListItemText>アカウント管理</ListItemText>
                  </ListItem>
                  <ListItem>
                    <ListItemText>ゲストアカウント管理</ListItemText>
                  </ListItem>
                  <ListItem>
                    <ListItemText>権限管理</ListItemText>
                  </ListItem>
                  <ListItem>
                    <ListItemText>シングルサインオン設定</ListItemText>
                  </ListItem>
                  <ListItem>
                    <ListItemText>IP制御設定</ListItemText>
                  </ListItem>
                  <ListItem>
                    <ListItemText>監査ログビューアー</ListItemText>
                  </ListItem>
                </List>
              </ListItem>
              <ListItem>
                <ListItemText>ユーザー機能</ListItemText>
                <List component="ul" className={classes.innerList}>
                  <ListItem>
                    <ListItemText>プロジェクト作成</ListItemText>
                  </ListItem>
                  <ListItem>
                    <ListItemText>ガントチャート</ListItemText>
                  </ListItem>
                  <ListItem>
                    <ListItemText>かんばん</ListItemText>
                  </ListItem>
                  <ListItem>
                    <ListItemText>
                      コラボレーション（ユーザー招待、ゲスト招待、URL共有）
                    </ListItemText>
                  </ListItem>
                  <ListItem>
                    <ListItemText>分析</ListItemText>
                  </ListItem>
                  <ListItem>
                    <ListItemText>フォルダ管理</ListItemText>
                  </ListItem>
                </List>
              </ListItem>
            </List>
            <Typography variant="body1">
              ※ゲストユーザーは招待されたプロジェクト内の機能のみを利用できます。権限管理にて管理者権限を付与することやプロジェクトの管理者になることはできません。
            </Typography>
          </Container>

          <Container disableGutters maxWidth={false}>
            <Typography variant="h3">
              2.2 環境構築およびアカウントの発行
            </Typography>
            <Typography variant="body1">
              お申し込みから1週間ほどでお申し込み時に登録いただいたメールアドレスあてに、アクセスURLと管理者アカウントの初期ログインIDをお送りいたします。2ユーザー目以降については、管理者アカウントにてお客様ご自身で登録いただけます。
            </Typography>
          </Container>

          <Container disableGutters maxWidth={false}>
            <Typography variant="h3">2.3 サポートサービス</Typography>
            <Typography variant="body1">
              お申し込みいただきましたメールアドレスあてに機能マニュアル、管理者マニュアルおよび問い合わせ先メールアドレスをお送りいたします。ご不明点がありましたら問い合わせ先メールアドレスまでお問い合わせください。3営業日をめどに１次回答いたします。
            </Typography>
          </Container>

          <Container disableGutters maxWidth={false}>
            <Typography variant="h3">2.4 利用料金</Typography>
            <Container disableGutters maxWidth={false}>
              <Typography variant="h4">【標準サービス】</Typography>
              <Typography variant="body1">[月額契約の場合]</Typography>
              <List component="ul">
                <ListItem>
                  <ListItemText>
                    月額利用料：960円（税抜）/ライセンス
                  </ListItemText>
                  <List component="ul" className={classes.noteList}>
                    <ListItem>
                      <ListItemText>
                        初回月額利用料に初期構築(利用環境構築)費用を含みます。
                      </ListItemText>
                    </ListItem>
                  </List>
                </ListItem>
              </List>
              <Typography variant="body1">[年額契約の場合]</Typography>
              <List component="ul">
                <ListItem>
                  <ListItemText>
                    年額利用料：9,600円（税抜）/ライセンス
                  </ListItemText>
                  <List component="ul" className={classes.noteList}>
                    <ListItem>
                      <ListItemText>
                        月額利用料の2ヶ月分を割り引いた金額になります。
                      </ListItemText>
                    </ListItem>
                    <ListItem>
                      <ListItemText>
                        初回年額利用料に初期構築(利用環境構築)費用を含みます。
                      </ListItemText>
                    </ListItem>
                  </List>
                </ListItem>
              </List>
              <List component="ul" className={classes.note}>
                <ListItem>
                  <ListItemText>
                    「契約ライセンス数×10GB」の容量制限があります。容量制限は全ユーザーで利用できる総量となります。
                  </ListItemText>
                </ListItem>
                <ListItem>
                  <ListItemText>
                    Project
                    Boardライセンス数は、一般ユーザーのアカウント数、及び、ゲストユーザーのアカウント数の総数とします。
                  </ListItemText>
                </ListItem>
                <ListItem>
                  <ListItemText>
                    利用料金は、契約ライセンス数に対して発生します。ライセンスは、アカウントの状態によって自動付与され、アカウントの状態が有効の場合はライセンスが付与された状態となります。
                  </ListItemText>
                </ListItem>
              </List>

              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>ライセンス</TableCell>
                      <TableCell>詳細</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell component="th" scope="row">
                        Project Boardライセンス
                      </TableCell>
                      <TableCell>
                        <span style={{ display: 'block' }}>
                          Project
                          Boardのアカウント管理機能に登録されているアカウントの中で、アカウントの状態が「有効」であるユーザーがライセンスの対象となります。
                        </span>
                        <span style={{ display: 'block' }}>
                          「有効」アカウント数が契約ライセンス数を超えない限り、アカウントの状態の変更は同一月内に何度でも行うことができます。
                        </span>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Container>
          </Container>

          <Container disableGutters maxWidth={false}>
            <Typography variant="h3">2.5 算定方法およびお支払い</Typography>

            <Container disableGutters maxWidth={false}>
              <Typography variant="h4">算定方法</Typography>
              <Typography variant="body1">[月額契約の場合]</Typography>
              <List component="ul">
                <ListItem>
                  <ListItemText>
                    利用規約に定める本サービス利用開始日から、利用料金が発生します
                  </ListItemText>
                </ListItem>
                <ListItem>
                  <ListItemText>
                    毎月末時点の契約ライセンス数に応じて当月の利用料金を確定します
                  </ListItemText>
                </ListItem>
                <ListItem>
                  <ListItemText>
                    本サービス利用開始日が月の途中の場合、当該月の利用料金は日割り計算となります
                  </ListItemText>
                  <List component="ul" className={classes.innerList}>
                    <ListItem>
                      <ListItemText>
                        当該月の利用料金 ＝｛月額利用料－（月額利用料 ×
                        利用しない日数 ÷ 暦日数）｝ × 契約ライセンス数
                      </ListItemText>
                    </ListItem>
                    <ListItem>
                      <ListItemText>
                        ※当該月の初日から本サービス利用開始日の前日までの日数を「利用しない日数」とします。
                      </ListItemText>
                    </ListItem>
                    <ListItem>
                      <ListItemText>【例】</ListItemText>
                      <List component="ul">
                        <ListItem>
                          <ListItemText>
                            1月16日に、100ライセンスの利用を開始する場合
                          </ListItemText>
                          <List component="ul" className={classes.innerList}>
                            <ListItem>
                              <ListItemText>
                                1月16日～1月31日（16日分）
                              </ListItemText>
                              <ListItemText>
                                ＝｛960円－（960円×15日÷31日）｝×100ライセンス
                              </ListItemText>
                              <ListItemText>＝495円×100ライセンス</ListItemText>
                              <ListItemText>＝49,500円（税抜）</ListItemText>
                            </ListItem>
                          </List>
                        </ListItem>
                      </List>
                    </ListItem>
                  </List>
                </ListItem>
                <ListItem>
                  <ListItemText>
                    月の途中でライセンス数を追加する場合、当該追加ライセンス数分の利用料金は、日割り計算となります
                  </ListItemText>
                  <List component="ul" className={classes.innerList}>
                    <ListItem>
                      <ListItemText>
                        追加ライセンス数分の利用料金
                        ＝｛月額利用料－（月額利用料 × 利用しない日数 ÷
                        暦日数）｝ × 追加ライセンス数
                      </ListItemText>
                    </ListItem>
                    <ListItem>
                      <ListItemText>【例】</ListItemText>
                      <List component="ul">
                        <ListItem>
                          <ListItemText>
                            4月1日から4月30日までの月額契約期間中の4月16日に、追加で100ライセンスの利用を開始する場合
                          </ListItemText>
                          <List component="ul" className={classes.innerList}>
                            <ListItem>
                              <ListItemText>
                                4月16日～4月30日（15日分）
                              </ListItemText>
                              <ListItemText>
                                ＝｛960円－（960円×15日÷30日）｝×100ライセンス
                              </ListItemText>
                              <ListItemText>＝480円×100ライセンス</ListItemText>
                              <ListItemText>＝48,000円（税抜）</ListItemText>
                            </ListItem>
                          </List>
                        </ListItem>
                      </List>
                    </ListItem>
                  </List>
                </ListItem>
                <ListItem>
                  <ListItemText>
                    日割り計算は先乗後除で行い、小数点は四捨五入とします
                  </ListItemText>
                </ListItem>
                <ListItem>
                  <ListItemText>
                    契約期間の途中で利用契約が終了する場合における利用料金は、日割り計算による減額又は返金は行わず、利用契約の終了日から当該利用契約が終了しなかった場合における契約期間満了日までの利用料金も発生します
                  </ListItemText>
                </ListItem>
              </List>
              <Typography variant="body1">[年額契約の場合]</Typography>
              <List component="ul">
                <ListItem>
                  <ListItemText>
                    利用規約に定める本サービス利用開始日から、利用料金が発生します
                  </ListItemText>
                </ListItem>
                <ListItem>
                  <ListItemText>
                    契約期間満了日が属する月（以下、契約期間満了月）の前月末時点の契約ライセンス数に応じて翌契約期間の利用料金を確定します
                  </ListItemText>
                </ListItem>
                <ListItem>
                  <ListItemText>
                    本サービス利用開始日が月の途中の場合、初年度の利用料金は、当該月の利用料金（月額利用料を日割り計算した金額（先乗後除、小数点は四捨五入））に2か月分の割引率を乗じた金額（小数点は四捨五入）を、年額利用料に加算した金額となります
                  </ListItemText>
                  <List component="ul" className={classes.innerList}>
                    <ListItem>
                      <ListItemText>【例】</ListItemText>
                      <List component="ul">
                        <ListItem>
                          <ListItemText>
                            1月16日に、100ライセンスの利用を開始する場合（初年度の契約期間は1月16日から翌年1月末日までとなり、次年度以降の契約期間は2月1日から翌年1月末日までとなります）
                          </ListItemText>
                          <List component="ul" className={classes.innerList}>
                            <ListItem>
                              <ListItemText>
                                ①1月16日～1月31日（16日分）
                              </ListItemText>
                              <ListItemText>
                                ＝｛月額利用料－(月額利用料×利用しない日数÷暦日数）｝×2か月分の割引率×契約ライセンス数
                              </ListItemText>
                              <ListItemText>
                                ＝｛960円－（960円×15日÷31日）｝×（10か月÷12か月）×100ライセンス
                              </ListItemText>
                              <ListItemText>
                                ＝495円×（10か月÷12か月）×100ライセンス
                              </ListItemText>
                              <ListItemText>＝413円×100ライセンス</ListItemText>
                              <ListItemText>＝41,300円（税抜）</ListItemText>
                            </ListItem>
                          </List>
                          <List component="ul" className={classes.innerList}>
                            <ListItem>
                              <ListItemText>
                                ②2月1日～翌年1月31日（12か月分）
                              </ListItemText>
                              <ListItemText>
                                ＝年額利用料×契約ライセンス数
                              </ListItemText>
                              <ListItemText>
                                ＝9,600円×100ライセンス
                              </ListItemText>
                              <ListItemText>＝960,000円（税抜）</ListItemText>
                            </ListItem>
                          </List>
                          <List component="ul" className={classes.innerList}>
                            <ListItem>
                              <ListItemText>
                                初年度の利用料金＝①＋②＝1,001,300円（税抜）（本サービス利用開始日が月の途中なので、支払期日は翌月2月末日）
                              </ListItemText>
                            </ListItem>
                          </List>
                        </ListItem>
                      </List>
                    </ListItem>
                  </List>
                </ListItem>
                <ListItem>
                  <ListItemText>
                    本サービス利用開始日後にライセンス数を追加する場合、当該追加ライセンス数分の初回利用料金は、当該追加月の利用料金（月の途中で追加した場合は月額利用料を日割り計算した金額（先乗後除、小数点は四捨五入））に2か月分の割引率を乗じた金額（小数点は四捨五入）を、月額利用料に当該追加月の翌月から契約期間満了月までの月数を乗じて2か月分の割引率を乗じた金額（小数点は四捨五入）（当該追加月が契約期間満了月に該当する場合は、当該追加月の翌契約期間の年額利用料となります）に加算した金額を一括でお支払いいただきます
                  </ListItemText>
                  <List component="ul" className={classes.innerList}>
                    <ListItem>
                      <ListItemText>【例】</ListItemText>
                      <List component="ul">
                        <ListItem>
                          <ListItemText>
                            [追加月が契約期間満了月以外の場合]
                          </ListItemText>
                          <ListItemText>
                            1月1日から12月31日までの年額契約期間中の4月16日に、追加で100ライセンスの利用を開始する場合
                          </ListItemText>
                          <List component="ul" className={classes.innerList}>
                            <ListItem>
                              <ListItemText>
                                ①4月16日～4月30日（15日分）
                              </ListItemText>
                              <ListItemText>
                                ＝｛月額利用料－(月額利用料×利用しない日数÷暦日数）｝×2か月分の割引率×追加ライセンス数
                              </ListItemText>
                              <ListItemText>
                                ＝｛960円－（960円×15日÷30日）｝×（10か月÷12か月）×100ライセンス
                              </ListItemText>
                              <ListItemText>
                                ＝480円×（10か月÷12か月）×100ライセンス
                              </ListItemText>
                              <ListItemText>＝400円×100ライセンス</ListItemText>
                              <ListItemText>＝40,000円（税抜）</ListItemText>
                            </ListItem>
                          </List>
                          <List component="ul" className={classes.innerList}>
                            <ListItem>
                              <ListItemText>
                                ②5月1日～12月31日（8か月分）
                              </ListItemText>
                              <ListItemText>
                                ＝（月額利用料×利用月数）×2か月分の割引率×追加ライセンス数
                              </ListItemText>
                              <ListItemText>
                                ＝6,400円×100ライセンス
                              </ListItemText>
                              <ListItemText>＝640,000円（税抜）</ListItemText>
                            </ListItem>
                          </List>
                          <List component="ul" className={classes.innerList}>
                            <ListItem>
                              <ListItemText>
                                追加ライセンス数分の初回利用料金＝①＋②＝680,000円（税抜）（支払期日は翌月5月末日）
                              </ListItemText>
                            </ListItem>
                          </List>
                        </ListItem>
                      </List>
                      <List component="ul">
                        <ListItem>
                          <ListItemText>
                            [追加月が契約期間満了月に該当する場合]
                          </ListItemText>
                          <ListItemText>
                            1月1日から12月31日までの年額契約期間中の12月16日に、追加で100ライセンスの利用を開始する場合
                          </ListItemText>
                          <List component="ul" className={classes.innerList}>
                            <ListItem>
                              <ListItemText>
                                ①12月16日～12月31日（16日分）
                              </ListItemText>
                              <ListItemText>
                                ＝｛月額利用料－(月額利用料×利用しない日数÷暦日数）｝×2か月分の割引率×追加ライセンス数
                              </ListItemText>
                              <ListItemText>
                                ＝｛960円－（960円×15日÷31日）｝×（10か月÷12か月）×100ライセン
                              </ListItemText>
                              <ListItemText>
                                ＝495円×（10か月÷12か月）×100ライセンス
                              </ListItemText>
                              <ListItemText>＝413円×100ライセンス</ListItemText>
                              <ListItemText>＝41,300円（税抜）</ListItemText>
                            </ListItem>
                          </List>
                          <List component="ul" className={classes.innerList}>
                            <ListItem>
                              <ListItemText>
                                ②翌年1月1日～12月31日（12か月分）
                              </ListItemText>
                              <ListItemText>
                                ＝年額利用料×契約ライセンス数
                              </ListItemText>
                              <ListItemText>
                                ＝9,600円×100ライセンス
                              </ListItemText>
                              <ListItemText>＝960,000円（税抜）</ListItemText>
                            </ListItem>
                          </List>
                          <List component="ul" className={classes.innerList}>
                            <ListItem>
                              <ListItemText>
                                追加ライセンス数分の初回利用料金＝①＋②＝1,001,300円（税抜）（支払期日は翌月にあたる翌年1月末日）
                              </ListItemText>
                            </ListItem>
                          </List>
                        </ListItem>
                      </List>
                    </ListItem>
                  </List>
                </ListItem>
                <ListItem>
                  <ListItemText>
                    契約期間の途中で利用契約が終了する場合における利用料金は、日割り計算による減額又は返金は行わず、利用契約の終了日から当該利用契約が終了しなかった場合における契約期間満了日までの利用料金も発生します
                  </ListItemText>
                </ListItem>
              </List>
            </Container>

            <Container disableGutters maxWidth={false}>
              <Typography variant="h4">お支払い</Typography>

              <Container disableGutters maxWidth={false}>
                <Typography variant="h5">【請求書支払】</Typography>
                <Typography variant="body1">[月額契約の場合]</Typography>
                <List component="ul">
                  <ListItem>
                    <ListItemText>
                      毎月末締め、翌月末払い（後払い）
                    </ListItemText>
                  </ListItem>
                  <ListItem>
                    <ListItemText>
                      お支払いが発生する月の8営業日以内にお支払いのご案内をお送りします。
                    </ListItemText>
                  </ListItem>
                </List>
                <Typography variant="body1">[年額契約の場合]</Typography>
                <List component="ul">
                  <ListItem>
                    <ListItemText>
                      契約期間満了月の前月末締め、契約期間満了月の月末に次年度分をお支払い（前払い）
                    </ListItemText>
                    <List component="ul" className={classes.innerList}>
                      <ListItem>
                        <ListItemText>【例】</ListItemText>
                        <List component="ul">
                          <ListItem>
                            <ListItemText>
                              契約期間が1月1日から12月31日までの場合、契約期間満了月の前月にあたる11月末日を毎年締め日として請求額が決定し、契約期間満了月にあたる12月末日までに次年度分をお支払いいただきます。
                            </ListItemText>
                          </ListItem>
                        </List>
                      </ListItem>
                    </List>
                  </ListItem>
                  <ListItem>
                    <ListItemText>
                      初年度分の支払期日は、本サービス利用開始日が月の初日の場合は当月末日、月の途中の場合は翌月末日となります
                    </ListItemText>
                    <List component="ul" className={classes.innerList}>
                      <ListItem>
                        <ListItemText>【例】</ListItemText>
                        <List component="ul">
                          <ListItem>
                            <ListItemText>
                              本サービス利用開始日が2022年1月1日の場合、初年度の契約期間は2022年1月1日から2022年12月31日までとなり、2021年12月31日を締め日として利用料金を確定し、支払期日は2022年1月末日となります。
                            </ListItemText>
                          </ListItem>
                          <ListItem>
                            <ListItemText>
                              本サービス利用開始日が2022年1月16日の場合、初年度の契約期間は2022年1月16日から2023年1月31日までとなり、2022年1月15日を締め日として利用料金を確定し、支払期日は2022年2月末日となります。
                            </ListItemText>
                          </ListItem>
                        </List>
                      </ListItem>
                    </List>
                  </ListItem>
                  <ListItem>
                    <ListItemText>
                      追加ライセンス数分の初回利用料金の支払期日は、当該追加月の翌月末日となります。次回以降は、既存ライセンス数と合算して契約期間満了月の前月末締め、契約期間満了月の月末に次年度分をお支払い（前払い）となります
                    </ListItemText>
                    <List component="ul" className={classes.innerList}>
                      <ListItem>
                        <ListItemText>【例】</ListItemText>
                        <List component="ul">
                          <ListItem>
                            <ListItemText>
                              [追加月が契約期間満了月以外の場合]
                            </ListItemText>
                            <List component="ul" className={classes.innerList}>
                              <ListItem>
                                <ListItemText>
                                  契約期間が1月1日から12月31日までで既に500ライセンスを利用している場合、2022年10月10日に200ライセンスを追加すると、追加する200ライセンス分の初回利用料金（2022年10月分の日割り＋2022年11月～12月分）の支払期日は2022年11月末日となり、次回2023年度分のお支払いは、既存500ライセンスの2023年度分のお支払いと合算して2022年12月末日に年額利用料金をお支払いいただきます。
                                </ListItemText>
                              </ListItem>
                            </List>
                          </ListItem>
                          <ListItem>
                            <ListItemText>
                              [追加月が契約期間満了月に該当する場合]
                            </ListItemText>
                            <List component="ul" className={classes.innerList}>
                              <ListItem>
                                <ListItemText>
                                  契約期間が1月1日から12月31日までで既に500ライセンスを利用している場合、2022年12月10日に200ライセンスを追加すると、追加する200ライセンス分の初回利用料金（2022年12月分の日割り＋2023年1月～12月分（2023年度分））の支払期日は2023年1月末日となり、次回2024年度分のお支払いは、既存500ライセンスの2024年度分のお支払いと合算して2023年12月末日に年額利用料金をお支払いいただきます。
                                </ListItemText>
                              </ListItem>
                            </List>
                          </ListItem>
                        </List>
                      </ListItem>
                    </List>
                  </ListItem>
                </List>
                <Typography variant="body1">
                  ※請求はマネーフォワードケッサイ株式会社による「マネーフォワード
                  ケッサイ」サービスにて行われます
                </Typography>
              </Container>

              <Container disableGutters maxWidth={false}>
                <Typography variant="h5">
                  【クレジットカード払い（請求書有）】
                </Typography>
                <Typography variant="body1">[月額契約の場合]</Typography>
                <List component="ul">
                  <ListItem>
                    <ListItemText>
                      初回のご請求金額は、クレジットカード払いの正式登録が完了すると同時に、初回の決済が行われます。
                    </ListItemText>
                  </ListItem>
                  <ListItem>
                    <ListItemText>
                      二回目のお支払い以降は、毎月1日にお支払いのご案内をお送りし、8日に決済が行われます。
                    </ListItemText>
                  </ListItem>
                  <ListItem>
                    <ListItemText>
                      ライセンス追加のご請求金額は、ライセンス追加登録が完了すると同時に、当月分の決済が行われ、翌月分以降は既存ライセンスと合算して請求及び決済が行われます。
                    </ListItemText>
                  </ListItem>
                </List>
                <Typography variant="body1">
                  ※請求はストライプジャパン株式会社（以下、ストライプ）による「Payments」（以下、Payments）サービスにて行われます。
                </Typography>
                <Typography variant="body1">
                  ※お客様は、当社がお客様を代理してPaymentsを通じてクレジットカード決済を行うこと、並びに、当該クレジットカード決済の処理はストライプ及びストライプの世界各国における関連会社が行うことについて同意するものとします。
                </Typography>
              </Container>
            </Container>
          </Container>
        </Container>

        <Container disableGutters maxWidth={false}>
          <Typography variant="h2">3. 商標について</Typography>
          <Typography variant="body1">
            本書に掲載されている各社名あるいは各製品名は、各社の登録商標または商標です。
          </Typography>
          <List component="ul">
            <ListItem>
              <ListItemText>
                マネーフォワードケッサイは、マネーフォワードケッサイ株式会社の登録商標または商標です。
              </ListItemText>
            </ListItem>
          </List>
        </Container>

        <Container disableGutters maxWidth={false}>
          <Typography variant="h2">付則</Typography>
          <Typography>
            改正後の本仕様書は、2022年6月29日より効力を有するものとします。
          </Typography>
          <Typography className={classes.lineBreak}>
            <span>初版施行日：2021年7月13日</span>
            <span>
              <Link
                component={RouterLink}
                to="/pjb/spec-archive/service-spec-01-20210713"
              >
                第１回改正施行日：2021年7月13日
              </Link>
            </span>
            <span>
              <Link
                component={RouterLink}
                to="/pjb/spec-archive/service-spec-02-20211223"
              >
                第２回改正施行日：2021年12月23日
              </Link>
            </span>
            <span>
              <Link
                component={RouterLink}
                to="/pjb/spec-archive/service-spec-03-20220318"
              >
                第３回改正施行日：2022年3月18日
              </Link>
            </span>
            <span>第４回改正施行日：2022年6月29日</span>
          </Typography>
        </Container>
      </Terms>
    </>
  );
};

export default ServiceSpec;
